import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, FileText, Calendar, DollarSign, Users, CheckCircle2, Upload, MessageSquare, ChevronRight, AlertCircle, X, Zap, Loader2 } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme.jsx';
import Modal from '../common/Modal.jsx';
import api from '../../services/api'
import { stagesFromDeliverables } from '../../utils/templateShape'
import DraftBar from '../common/DraftBar.jsx'
import { saveFileDraft, loadFileDraft } from '../../services/draftStore';
import { formatDate, formatDateTime } from '../../utils/helpers';
import { useTranslation } from 'react-i18next'

// Confidence badge styling for TOR-extracted fields
const CONFIDENCE_BADGE = {
  extracted: { label: 'auto-filled from TOR', cls: 'bg-green-100 text-green-700 border-green-200' },
  inferred: { label: 'inferred — verify', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  missing: { label: 'missing — please fill', cls: 'bg-red-100 text-red-700 border-red-200' },
};

const Badge = ({ flag }) => {
  if (!flag) return null;
  const cfg = CONFIDENCE_BADGE[flag];
  if (!cfg) return null;
  return <span className={`ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded border ${cfg.cls}`}>{cfg.label}</span>;
};

// Parse a free-typed date (YYYY-MM-DD, DD/MM/YYYY, DD-Mon-YYYY, or anything Date can read) to
// ISO YYYY-MM-DD. Returns the input unchanged if it can't parse, so validation can flag it.
const parseDateToISO = (raw) => {
  const s = (raw || '').trim();
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const months = { jan:'01',feb:'02',mar:'03',apr:'04',may:'05',jun:'06',jul:'07',aug:'08',sep:'09',oct:'10',nov:'11',dec:'12' };
  let m;
  if ((m = s.match(/^(\d{1,2})[-/ ]([A-Za-z]{3,})[-/ ](\d{4})$/))) {
    const mm = months[m[2].slice(0,3).toLowerCase()];
    if (mm) return `${m[3]}-${mm}-${m[1].padStart(2,'0')}`;
  }
  if ((m = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/))) {
    return `${m[3]}-${m[2].padStart(2,'0')}-${m[1].padStart(2,'0')}`;
  }
  const d = new Date(s);
  if (!isNaN(d)) return d.toISOString().slice(0,10);
  return s;
};

const FormInput = ({ label, required, type = 'text', value, onChange, placeholder, disabled, min, step, rows, isDark }) => {
  const baseClasses = `
    w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 ease-out
    focus:outline-none focus:ring-4 focus:ring-blue-500/10
    disabled:opacity-60 disabled:cursor-not-allowed
    ${isDark
      ? 'border-gray-700 bg-gray-800/50 text-white placeholder-gray-500 focus:border-blue-400 focus:bg-gray-800 disabled:bg-gray-800'
      : 'border-gray-200 bg-gray-50 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:bg-white disabled:bg-gray-100'}
  `;

  return (
    <div className="group">
      <label className={`block text-sm font-semibold mb-2 flex items-center gap-1 transition-colors ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      {rows ? (
        <textarea
          rows={rows}
          className={`${baseClasses} resize-none`}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
        />
      ) : (
        <input
          type={type}
          className={baseClasses}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          min={min}
          step={step}
        />
      )}
    </div>
  );
};

// Keyboard-friendly date field: ONE clean text box you can type into freely (accepts
// YYYY-MM-DD, DD/MM/YYYY, DD-Mon-YYYY — parsed to ISO on blur), with a calendar icon inside
// that opens the native date picker for mouse users. Stores/emits ISO (YYYY-MM-DD).
// `error` (string) shows an inline message explaining exactly what's wrong.
const DateField = ({ label, required, value, onChange, error, isDark, max, hint }) => {
  const { t } = useTranslation('projectForms')
  // Show the friendly "07-Jun-2026" in the box (parseDateToISO reads it back), while the
  // stored/emitted value stays ISO (YYYY-MM-DD) for the backend.
  const pretty = (iso) => (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso) ? formatDate(iso) : (iso || ''));
  const [text, setText] = React.useState(pretty(value));
  React.useEffect(() => { setText(pretty(value)); }, [value]);
  const pickerRef = React.useRef(null);
  const commit = (raw) => {
    const iso = parseDateToISO(raw);
    setText(pretty(iso));
    onChange({ target: { value: iso } });
  };
  const inputCls = `
    w-full pl-4 pr-11 py-3 rounded-xl border-2 transition-all duration-200 ease-out
    focus:outline-none focus:ring-4 focus:ring-blue-500/10
    ${error
      ? 'border-red-400 focus:border-red-500'
      : isDark
        ? 'border-gray-700 bg-gray-800/50 text-white placeholder-gray-500 focus:border-blue-400 focus:bg-gray-800'
        : 'border-gray-200 bg-gray-50 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:bg-white'}`;
  return (
    <div className="group">
      <label className={`block text-sm font-semibold mb-2 flex items-center gap-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
        {label}{required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <input
          type="text"
          className={inputCls}
          value={text}
          placeholder={t('ph_date_long')}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => commit(text)}
          required={required}
        />
        {/* Calendar icon opens the native picker; the hidden date input is the picker source. */}
        <button
          type="button"
          onClick={() => { const el = pickerRef.current; if (el) { el.showPicker ? el.showPicker() : el.focus(); } }}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-blue-600"
          aria-label={`Open ${label} calendar`}
        >
          <Calendar className="w-5 h-5" />
        </button>
        <input
          ref={pickerRef}
          type="date"
          tabIndex={-1}
          aria-hidden="true"
          value={value || ''}
          max={max || undefined}
          onChange={(e) => { setText(pretty(e.target.value)); onChange(e); }}
          className="absolute right-2 bottom-0 w-0 h-0 opacity-0 pointer-events-none"
        />
      </div>
      {error && (
        <p className="flex items-center gap-1 text-red-500 text-xs mt-1.5">
          <AlertCircle className="w-3 h-3 shrink-0" /> {error}
        </p>
      )}
      {!error && hint && (
        <p className={`text-[11px] mt-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{hint}</p>
      )}
    </div>
  );
};

// Compact inline date input (used per milestone row). Same behaviour as DateField — type freely
// or use the calendar icon; displays "07-Jun-2026" but emits ISO — just without the label block.
const InlineDate = ({ value, onChange, disabled, isDark }) => {
  const { t } = useTranslation('projectForms')
  const pretty = (iso) => (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso) ? formatDate(iso) : (iso || ''));
  const [text, setText] = React.useState(pretty(value));
  React.useEffect(() => { setText(pretty(value)); }, [value]);
  const pickerRef = React.useRef(null);
  return (
    <div className="relative flex-1">
      <input
        type="text"
        value={text}
        disabled={disabled}
        placeholder={t('ph_date_short')}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => { const iso = parseDateToISO(text); setText(pretty(iso)); onChange(iso); }}
        className={`w-full pl-3 pr-9 py-1.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/10 ${isDark ? 'border-gray-600 bg-gray-800 text-white focus:border-blue-500' : 'border-gray-200 bg-white text-gray-900 focus:border-blue-500'}`}
      />
      <button
        type="button"
        disabled={disabled}
        onClick={() => { const el = pickerRef.current; if (el) { el.showPicker ? el.showPicker() : el.focus(); } }}
        className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-blue-600"
        aria-label="Open due-date calendar"
      >
        <Calendar className="w-4 h-4" />
      </button>
      <input
        ref={pickerRef}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        value={value || ''}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="absolute right-1.5 bottom-0 w-0 h-0 opacity-0 pointer-events-none"
      />
    </div>
  );
};

const FormSelect = ({ label, required, value, onChange, options, placeholder, isDark }) => (
  <div className="group">
    <label className={`block text-sm font-semibold mb-2 flex items-center gap-1 transition-colors ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
      {label}
      {required && <span className="text-red-500">*</span>}
    </label>
    <div className="relative">
      <select
        value={value}
        onChange={onChange}
        className={`
          w-full px-4 py-3 rounded-xl border-2 appearance-none cursor-pointer
          transition-all duration-200 ease-out
          focus:outline-none focus:ring-4 focus:ring-blue-500/10
          ${isDark
            ? 'border-gray-700 bg-gray-800/50 text-white focus:border-blue-400 focus:bg-gray-800'
            : 'border-gray-200 bg-gray-50 text-gray-900 focus:border-blue-500 focus:bg-white'}
          ${!value ? (isDark ? 'text-gray-500' : 'text-gray-400') : ''}
        `}
        required={required}
      >
        <option value="" disabled>{placeholder}</option>
        {options.map((opt, idx) => (
          <option key={idx} value={opt.value} className={isDark ? 'bg-gray-800' : 'bg-white'}>
            {opt.label}
          </option>
        ))}
      </select>
      <div className={`absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
        <ChevronRight className="w-5 h-5 rotate-90" />
      </div>
    </div>
  </div>
);

const ProjectModal = ({ isOpen, onClose, editingProject, initialParentId = '', users = [], currentUser, onSubmit, presetTemplate = null, templateAlreadyOffered = false }) => {
  const { t: tc } = useTranslation('common')
  const { t } = useTranslation('projectForms')
  // Draft autosave is PER USER — a browser-global key leaked one user's draft to the
  // next person who logged in on the same machine.
  // Scoped per USER and per PARENT: a sub-project draft under one parent never leaks into a
  // top-level create or another parent's sub-project.
  const draftKey = `project_draft:${currentUser?.id || currentUser?.email || 'anon'}${initialParentId ? `:sub:${initialParentId}` : ''}`;
  const { isDark } = useTheme();
  const [step, setStep] = useState(1);
  const [showDraftPromptModal, setShowDraftPromptModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [isAlertError, setIsAlertError] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');
  const projectFormRef = useRef(null);

  const initialFormState = {
    name: '',
    projectNumber: '',
    description: '',
    jobTitle: '',
    purposeOfPosition: '',
    startDate: '',
    endDate: '',
    priority: '',
    budgetType: 'Fixed',
    hybridActuals: false,   // Hybrid: whether the "Actuals" part is shown alongside the estimated budget
    estimatedBudget: '',
    teamMemberIds: [],
    milestones: [],
    files: [],
    notes: [],
    consultantId: '',
    projectManagerId: '',
    external_ref: '',
    parentProjectId: ''   // v2 nested projects: create as a sub-project under this parent
  };

  const [formData, setFormData] = useState(initialFormState);

  // Starting from a template — the sibling of the TOR upload above it. A TOR is the ideal
  // start because it is the real document; a template is what you reach for when there isn't
  // one yet, and it supplies the SHAPE only: the stages this kind of work goes through.
  const [templates, setTemplates] = useState([]);
  const [usedTemplate, setUsedTemplate] = useState(null);
  useEffect(() => {
    if (!isOpen || editingProject) return;
    api.get('/templates/?kind=project')
      .then(r => setTemplates(r.data?.items || []))
      .catch(() => setTemplates([]));      // no templates is a normal answer, not an error
  }, [isOpen, editingProject]);

  // What a template does to a form, as a plain value rather than a state update — so it can be
  // used both when building a fresh form and when somebody picks a template inside one.
  const withTemplate = (form, tmpl) => ({
    ...form,
    // Its default only fills what the form is still sitting on untouched, so a template
    // cannot overrule a decision somebody has already made on this screen.
    budgetType: tmpl.defaults?.budget_type || form.budgetType,
    milestones: (tmpl.milestones || [])
      .filter(m => (m?.title || '').trim())
      .map(m => ({
        // The wizard calls a milestone's title "description".
        description: m.title,
        status: 'Pending',
        // No date and no figure. Both are decided per project, and a template that guessed
        // would put money against a stage nobody agreed to — the deadline is still required
        // before this project can be created, which is the intended prompt.
        amount: '',
        deadline: '',
        briefFile: null,
        mode: (m.payment_mode === 'Actual') ? 'actual' : 'fixed',
      })),
  });


  const applyTemplate = (tmpl) => {
    setFormData(prev => withTemplate(prev, tmpl));
    setUsedTemplate(tmpl);
  };
  // Top-level projects a new project can be nested under (fetched when the modal opens).
  const [parentOptions, setParentOptions] = useState([]);
  useEffect(() => {
    if (!isOpen) return;
    api.get('/projects/').then(r => {
      const rows = Array.isArray(r.data) ? r.data : [];
      // Only top-level projects (no parent) can be a parent; never self.
      setParentOptions(rows.filter(p => !p.parent_project_id && p.id !== editingProject?.id));
    }).catch(() => setParentOptions([]));
  }, [isOpen, editingProject?.id]);

  // A sub-project must finish inside its parent program: the End Date defaults to the parent's
  // and is capped there, but may be pulled earlier (finishing early is legitimate). The backend
  // enforces the same rule, since the inline editor and TOR upload also write end_date.
  const selectedParent = React.useMemo(() => {
    const pid = formData?.parentProjectId || initialParentId;
    if (!pid) return null;
    return parentOptions.find(p => String(p.id) === String(pid)) || null;
  }, [parentOptions, formData?.parentProjectId, initialParentId]);
  const parentEndDate = selectedParent?.end_date || selectedParent?.endDate || '';
  // Default the sub-project's end date to the parent's. Only fills when EMPTY, so it never
  // clobbers a date the user picked or one restored from a saved draft.
  useEffect(() => {
    if (!isOpen || !parentEndDate) return;
    setFormData(prev => (prev.endDate ? prev : { ...prev, endDate: parentEndDate }));
  }, [isOpen, parentEndDate]);

  // ─── TOR drag-and-drop state ─────────────────────────────────────────────
  const [torDragging, setTorDragging] = useState(false);
  const [torParsing, setTorParsing] = useState(false);
  const [torParsed, setTorParsed] = useState(false);
  const [torError, setTorError] = useState(null);
  // Paste-a-brief: most projects arrive as an email or a message, not a .docx.
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pastedText, setPastedText] = useState('');
  const [confidence, setConfidence] = useState({}); // { name: 'extracted', description: 'extracted', ... }
  const [aiRecs, setAiRecs] = useState(null);        // AI consultant suggestions for the Assign step
  const [aiRecsLoading, setAiRecsLoading] = useState(false);
  const fetchConsultantRecs = async () => {
    setAiRecsLoading(true); setAiRecs(null);
    try {
      const r = await api.post('/projects/recommend-consultants', {
        description: (formData.description || formData.name || '').trim(), limit: 6,
      });
      setAiRecs(r.data?.recommendations || []);
    } catch {
      setAiRecs([]);
    } finally {
      setAiRecsLoading(false);
    }
  };
  const [torKey, setTorKey] = useState(null);
  const torFileRef = useRef(null);

  // Fill the form from whatever the extractor returned. Shared deliberately: an uploaded .docx
  // and a pasted email go through the same backend extractor and must land in the form
  // identically, or the two paths drift the first time somebody adds a field.
  // THE SHAPE OF THE DOCUMENT, KEPT SO IT CAN BE REUSED.
  //
  // A client's Terms of Reference has a house format — the headings they always use and the
  // stages they always ask for. Read once and thrown away, the next project for the same client
  // starts from nothing again.
  //
  // Only the regex reader returns real headings; the AI reader hands back one blob of text under
  // "ai_full_text", which is not a format and is filtered out below rather than saved as one.
  const [torSections, setTorSections] = useState([]);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateSaved, setTemplateSaved] = useState(null);

  const saveTorAsTemplate = async () => {
    const suggested = formData.name ? `Like: ${formData.name}` : 'Terms of Reference';
    const name = window.prompt(
      'Save this document\u2019s shape as a template.' + String.fromCharCode(10) + String.fromCharCode(10)
      + 'Its headings and its stages are kept, with deadlines taken off. No dates, no money.',
      suggested);
    if (!name || !name.trim()) return;
    setSavingTemplate(true); setTorError(null);
    try {
      const { data } = await api.post('/templates/', {
        name: name.trim(),
        kind: 'project',
        description: `Taken from a Terms of Reference document.`,
        // The stages it asked for, without what any one project put against them.
        // The same shaper the Templates page uses, so one document saved from here and the
        // same document saved from there cannot produce different stages.
        milestones: stagesFromDeliverables(formData.milestones || []),
        // The headings the document itself used.
        fields: torSections.map((label, n) => ({
          key: `s${n}`, label, type: 'textarea', required: false, help: '',
        })),
      });
      setTemplateSaved(`Saved as "${data?.name || name.trim()}". It will be offered the next time
        somebody starts a project.`);
    } catch (e) {
      setTorError(e.response?.data?.detail || 'Could not save that as a template.');
    } finally { setSavingTemplate(false); }
  };

  const applyExtracted = (data) => {
    setFormData(prev => ({
      ...prev,
      name: data.proposed_name || prev.name,
      description: data.description || prev.description,
      jobTitle: data.job_title || prev.jobTitle,
      purposeOfPosition: data.preferred_specialization || prev.purposeOfPosition,
      startDate: data.start_date || prev.startDate,
      endDate: data.end_date || prev.endDate,
      priority: data.priority || prev.priority,
      estimatedBudget: data.suggested_budget != null ? String(data.suggested_budget) : prev.estimatedBudget,
      milestones: (data.deliverables || []).length
        ? (data.deliverables || []).map(d => ({
            title: d.title || '',
            description: d.title || '',
            deadline: d.deadline || '',
            amount: d.amount || '',
            status: 'Pending',
          }))
        : prev.milestones,
    }));

    // Headings only, and only when they are headings. The AI reader returns the whole document
    // under one key, which describes nothing about the format.
    setTorSections(Object.keys(data.raw_sections || {})
      .filter(k => k && k !== 'ai_full_text')
      .map(k => k.trim())
      .filter(k => k.length > 2 && k.length < 80));

    const f = data.confidence_flags || {};
    const ex = (v) => (v ? 'extracted' : 'missing');
    setConfidence({
      name: f.proposed_name || 'missing',
      description: f.description || 'missing',
      milestones: f.deliverables || 'missing',
      startDate: ex(data.start_date),
      endDate: ex(data.end_date),
      priority: ex(data.priority),
      estimatedBudget: ex(data.suggested_budget != null),
      purposeOfPosition: ex(data.preferred_specialization),
      jobTitle: ex(data.job_title),
    });
    setTorKey(data.tor_document_key || null);
    setTorParsed(true);
  };

  // Read a project out of PASTED TEXT — an email, a WhatsApp message, a scope note. Most briefs
  // never arrive as a tidy .docx, and pasting one into Word purely so ProTrack will read it is a
  // step with no purpose.
  // SAYING IT OUT LOUD, NOT ONLY TYPING IT.
  //
  // Somebody describing work they want done says it far more naturally than they write it, and
  // a project manager between meetings will not type six sentences on a phone. The browser does
  // the listening, so no audio is uploaded or stored anywhere — what arrives is text, which is
  // what the reader below wants anyway.
  const [listening, setListening] = useState(false);
  const [speechError, setSpeechError] = useState(null);
  const recogRef = useRef(null);
  const speechSupported = typeof window !== 'undefined'
    && !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  // Takes the setter for whatever box is being spoken into. There are two of them now — the
  // description and the milestones — and a second copy of the speech handling would have been a
  // second place for the same bug.
  const toggleDictation = (setTarget = setPastedText) => {
    if (listening) { recogRef.current?.stop(); return; }
    const Recog = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recog) {
      setSpeechError('This browser cannot listen. Chrome or Edge can — or simply type it.');
      return;
    }
    setSpeechError(null);
    const r = new Recog();
    r.continuous = true;
    r.interimResults = false;
    r.onresult = (e) => {
      let heard = '';
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        if (e.results[i].isFinal) heard += e.results[i][0].transcript;
      }
      // APPENDED, NEVER REPLACED. Somebody who typed three lines and then spoke a fourth must
      // not lose the three.
      if (heard.trim()) setTarget(prev => (prev ? prev.trimEnd() + ' ' : '') + heard.trim());
    };
    r.onerror = (e) => {
      // THE REASON, NOT A CATEGORY. Every failure used to land in one of two sentences, so
      // "blocked" was shown for a microphone that had just been allowed — and there was nothing
      // anywhere, console included, saying which of several quite different faults it was.
      const code = e?.error || 'unknown';
      console.warn('[dictation] SpeechRecognition error:', code, e);
      const said = {
        'not-allowed':
          'The microphone is blocked for this site — allow it in the address bar, then reload.',
        // Chrome's dictation is a Google service, not the microphone. Allowing the mic changes
        // nothing here, which is exactly why the old message sent people back to a setting that
        // was already correct.
        'service-not-allowed':
          'This browser will not reach its speech service — often a policy on a managed device, '
          + 'or a VPN. Typing works regardless.',
        'audio-capture':
          'No microphone was found. Check one is plugged in and not in use by another program.',
        'network': 'The speech service could not be reached. Check the connection, or type it.',
        'no-speech': 'Nothing was heard. Try again, closer to the microphone.',
        'aborted': null,   // the person stopped it; not a fault and not worth a message
      }[code];
      if (said !== null) {
        setSpeechError(said || `Dictation stopped (${code}). Typing works.`);
      }
      setListening(false);
    };
    r.onend = () => setListening(false);
    recogRef.current = r;
    setListening(true);
    r.start();
  };

  // ── What the reader did not find ────────────────────────────────────────────
  //
  // A project created with no milestones looks finished and is not: nothing can be offered to a
  // consultant, so nothing can be delivered or invoiced. It was silent before — the form simply
  // had an empty list, and the person submitting had no reason to look.
  const [msGapText, setMsGapText] = useState('');
  const [msGapParsing, setMsGapParsing] = useState(false);
  const [msGapDismissed, setMsGapDismissed] = useState(false);
  const [msGapError, setMsGapError] = useState(null);

  // Read milestones out of whatever they typed or spoke, using the same reader the TOR uses so
  // "three deliverables, 20 May, 4 June and 14 June" is understood the same way in both places.
  const applyMilestoneText = async () => {
    const text = (msGapText || '').trim();
    if (text.length < 10) { setMsGapError('Write a little more — a line per milestone is enough.'); return; }
    setMsGapParsing(true); setMsGapError(null);
    try {
      const { data } = await api.post('/projects/parse-text', { text });
      const found = (data.deliverables || []).map(d => ({
        title: d.title || '', description: d.title || '',
        deadline: d.deadline || '', amount: d.amount || '',
        status: 'Pending', briefFile: null, mode: 'fixed',
      }));
      if (!found.length) { setMsGapError('No milestones could be read from that. Try one per line.'); return; }
      setFormData(prev => ({
        ...prev,
        milestones: [...(prev.milestones || []), ...found],
        // Dates and priority often arrive in the same breath as the milestones. Taken only when
        // the form does not already have them — never overwriting something already decided.
        startDate: prev.startDate || data.start_date || '',
        endDate: prev.endDate || data.end_date || '',
        priority: prev.priority || data.priority || prev.priority,
      }));
      setMsGapText('');
    } catch (e) {
      setMsGapError(e.response?.data?.detail || 'Could not read that. Try one milestone per line.');
    } finally { setMsGapParsing(false); }
  };

  // EQUAL SHARES, NOT A GUESS AT EFFORT.
  //
  // Splitting "by the work" needs somebody to judge how much work each milestone is, and a title
  // does not say. A confident wrong split is worse than an obvious even one, because nobody
  // checks a number that looks considered. Even shares are predictable, and every figure stays
  // editable. The remainder goes on the last milestone so the parts always total the budget.
  const splitBudgetEvenly = () => {
    const list = formData.milestones || [];
    const total = parseFloat(formData.estimatedBudget) || 0;
    if (!list.length || total <= 0) return;
    const each = Math.floor((total / list.length) * 100) / 100;
    const allocated = each * (list.length - 1);
    setFormData(prev => ({
      ...prev,
      milestones: (prev.milestones || []).map((m, i) => ({
        ...m,
        amount: String(i === list.length - 1 ? Math.round((total - allocated) * 100) / 100 : each),
      })),
    }));
  };

  const handlePastedText = async () => {
    const text = (pastedText || '').trim();
    if (text.length < 40) {
      setTorError('Paste a bit more — there is not enough here to read a project from.');
      return;
    }
    setTorError(null);
    setTorParsing(true);
    try {
      const { data } = await api.post('/projects/parse-text', { text });
      applyExtracted(data);
      setPasteOpen(false);
    } catch (err) {
      setTorError(err.response?.data?.detail || 'Could not read that text.');
    } finally {
      setTorParsing(false);
    }
  };

  // Parse a TOR docx via backend and auto-fill the form
  const handleTorUpload = async (file) => {
    // A signed-off TOR is usually a PDF. Refusing them pushed the admin into converting the
    // file by hand, which is exactly where the headings the extractor relies on get lost.
    if (!/\.(docx|pdf)$/i.test(file?.name || '')) {
      setTorError('Upload a .docx or .pdf file.');
      return;
    }
    setTorError(null);
    setTorParsing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/projects/from-tor', fd);

      // Same applier the paste path uses, so both fill the form identically.
      applyExtracted(data);
    } catch (err) {
      setTorError(err.response?.data?.detail || 'Failed to parse TOR document');
    } finally {
      setTorParsing(false);
    }
  };

  // Compute which required fields are still missing after TOR fill — used for the "missing fields" alert
  const requiredFields = [
    { key: 'name', label: 'Project Name' },
    { key: 'description', label: 'Description' },
    // 'Job Title' and 'Purpose of Position' are optional (per Mam).
    { key: 'startDate', label: 'Start Date' },
    { key: 'endDate', label: 'End Date' },
    { key: 'priority', label: 'Priority' },
    // A fixed amount is required only for Fixed. Actual claims later with bills;
    // Hybrid uses per-milestone Fixed/Actuals, so its top budget is an optional estimate.
    ...(formData.budgetType === 'Fixed'
      ? [{ key: 'estimatedBudget', label: 'Estimated Budget' }]
      : []),
  ];
  const missingFields = requiredFields.filter(f => !formData[f.key] || String(formData[f.key]).trim() === '');

  // Milestones must fit inside the project's budget and timeline (Bharat's testing point).
  const budgetNum = parseFloat(formData.estimatedBudget) || 0;
  const milestonesTotal = (formData.milestones || []).reduce((s, m) => s + (parseFloat(m.amount) || 0), 0);
  const overBudget = formData.budgetType !== 'Actual' && budgetNum > 0 && milestonesTotal > budgetNum + 0.005;
  const dateOutOfRange = (formData.milestones || []).some(m =>
    m.deadline && (
      (formData.startDate && m.deadline < formData.startDate) ||
      (formData.endDate && m.deadline > formData.endDate)
    )
  );
  const milestoneIssue = overBudget
    ? `Milestone payments ($${milestonesTotal.toLocaleString()}) exceed the project budget ($${budgetNum.toLocaleString()}).`
    : dateOutOfRange
      ? 'One or more milestone dates fall outside the project start–end dates.'
      : null;

  // Load project data when editing — the API sends snake_case, the form uses camelCase,
  // so every field is mapped explicitly (previously only budgetType was, which made
  // Edit open a mostly blank form).
  useEffect(() => {
    if (editingProject) {
      const toDay = (v) => (v ? new Date(v).toISOString().split('T')[0] : '');
      setFormData({
        ...initialFormState,
        ...editingProject,
        budgetType: editingProject.budgetType || editingProject.budget_type || 'Fixed',
        jobTitle: editingProject.jobTitle || editingProject.job_title || '',
        purposeOfPosition: editingProject.purposeOfPosition || editingProject.purpose_of_position || '',
        estimatedBudget: String(editingProject.estimatedBudget ?? editingProject.totalBudget ?? editingProject.total_budget ?? '') || '',
        projectNumber: editingProject.projectNumber || editingProject.project_number || '',
        external_ref: editingProject.external_ref || '',
        projectManagerId: editingProject.projectManagerId || editingProject.project_manager_id || '',
        consultantId: editingProject.consultantId || editingProject.consultant_id || '',
        parentProjectId: editingProject.parentProjectId || editingProject.parent_project_id || '',
        startDate: toDay(editingProject.startDate || editingProject.start_date),
        endDate: toDay(editingProject.endDate || editingProject.end_date),
      });
      if (editingProject.id) {
        // Pre-populate the current team members so the picker reflects them.
        api.get(`/projects/${editingProject.id}/members`)
          .then(r => setFormData(prev => ({ ...prev, teamMemberIds: r.data?.member_ids || [] })))
          .catch(() => {})
        // Pre-populate the existing milestones so they can be reviewed/edited (not re-typed).
        api.get(`/projects/${editingProject.id}/milestones/`)
          .then(r => setFormData(prev => ({
            ...prev,
            milestones: (r.data || []).map(m => ({
              id: m.id,
              description: m.title || '',
              status: m.status || 'Pending',
              amount: m.payment_amount != null ? String(m.payment_amount) : '',
              deadline: m.due_date ? String(m.due_date).split('T')[0] : '',
              briefFile: null,
              mode: (m.payment_mode === 'Actual') ? 'actual' : 'fixed',
              locked: ['Accepted', 'Approved'].includes(m.scope_status || ''),
            })),
          })))
          .catch(() => {})
      }
      setStep(1);
    } else if (isOpen && !editingProject) {
      // One-time cleanup: drop the old browser-global draft key (pre per-user fix)
      // so nobody inherits another user's draft.
      localStorage.removeItem('project_draft');
      // Check for drafts only for new projects — scoped to THIS user
      // Cleared FIRST, always. Whether or not a draft is then offered, the modal must not open
      // still holding the last project's TOR, badges and attachment — which is what it did when
      // only the draft-less path reset anything.
      resetForm();
      if (localStorage.getItem(draftKey)) {
        setShowDraftPromptModal(true);
      }
    }
  }, [isOpen, editingProject, initialParentId]);

  // What a named draft stores, and what restoring one puts back. Shared with nothing else on
  // purpose: the moment two places decide what "the form state" is, one of them is wrong.
  // The uploaded TOR travels IN the draft, because the file is already on the server and
  // tor_document_key is just a reference to it. That makes it survive on any device — losing it
  // was never a storage problem, the key simply was not being saved.
  //
  // Real File objects cannot go here (not JSON, and far too big for a row), so they are parked
  // in IndexedDB against the draft id by onDraftSaved below.
  const collectDraft = () => ({
    ...formData,
    milestones: (formData.milestones || []).map(m => ({ ...m, briefFile: null })),
    files: [],
    reimbursements: [],
    _tor: { key: torKey, parsed: torParsed, confidence },
  })
  const applyDraft = async (payload, draftId) => {
    const p = payload || {}
    // Merged over the initial state rather than replacing it, so a draft saved before a field
    // existed does not leave that field undefined and crash an input on render.
    const { _tor, ...fields } = p
    setFormData({ ...initialFormState, ...fields })
    setStep(1)

    // The TOR: still on the server, so this is just restoring the reference and the green
    // "read from your TOR" badges that go with it.
    if (_tor?.key) {
      setTorKey(_tor.key)
      setTorParsed(!!_tor.parsed)
      setConfidence(_tor.confidence || {})
    }

    // Milestone brief files, if this is the same browser that saved them.
    if (draftId) {
      const local = await loadFileDraft(`project-files-u${currentUser?.id || 'anon'}-${draftId}`)
      const briefs = local?.briefs || {}
      if (Object.keys(briefs).length) {
        setFormData(f => ({
          ...f,
          milestones: (f.milestones || []).map((m, i) => briefs[i] ? { ...m, briefFile: briefs[i] } : m),
        }))
      }
    }
  }

  // Park the File objects against the draft id. Best-effort: if IndexedDB is unavailable the
  // draft still saves, it simply comes back without the attachments.
  const onDraftSaved = async (draftId) => {
    const briefs = {}
    ;(formData.milestones || []).forEach((m, i) => { if (m.briefFile) briefs[i] = m.briefFile })
    if (Object.keys(briefs).length) {
      await saveFileDraft(`project-files-u${currentUser?.id || 'anon'}-${draftId}`,
        { briefs, savedAt: new Date().toISOString() })
    }
  }


  // Safety net: while creating a NEW project, auto-save the form to localStorage on every
  // change (files stripped — they can't be serialised). If anything ever reloads the page,
  // reopening "Create Project" offers to restore the draft, so nothing is lost.
  useEffect(() => {
    if (!isOpen || editingProject) return;
    const hasContent = formData.name?.trim() || formData.description?.trim()
      || (formData.milestones?.length > 0) || (formData.teamMemberIds?.length > 0);
    if (!hasContent) return;
    try {
      localStorage.setItem(draftKey, JSON.stringify({
        ...formData,
        milestones: (formData.milestones || []).map(m => ({ ...m, briefFile: null })),
        files: [],
        reimbursements: [],
      }));
    } catch { /* ignore quota/serialise errors */ }
  }, [formData, isOpen, editingProject, draftKey]);

  const consultantsOptions = Array.isArray(users) ? users.filter(u => u.role === 'Consultant' || u.role === 'consultant') : [];
  const pmOptions = Array.isArray(users) ? users.filter(u => u.role === 'Project Manager') : [];
  const isAdmin = currentUser?.role === 'Admin';

  // EVERYTHING the form holds, not just formData.
  //
  // The form's state lives across eleven useState calls; resetting only formData left a fresh
  // project carrying the previous one's uploaded TOR, its green "auto-filled from TOR" badges
  // against fields nothing had filled, the paste box still open with text in it, and the wizard
  // sitting on step 3. A new project has to be new.
  const resetForm = () => {
    // A template chosen at the door is part of a fresh form, not something applied on
    // top of one. resetForm runs when the modal opens AND again on each draft-prompt
    // path, so anything applied outside it is wiped by whichever route the person takes.
    const fresh = initialParentId
      ? { ...initialFormState, parentProjectId: String(initialParentId) }
      : { ...initialFormState };
    setFormData(presetTemplate ? withTemplate(fresh, presetTemplate) : fresh);
    // And the line that says where the milestones came from, so it matches what is
    // actually in the form. Cleared when there is no template, so a previous project
    // cannot leave its confirmation behind on a fresh one.
    setUsedTemplate(presetTemplate || null);
    setStep(1);
    // the TOR extraction and everything it filled in
    setTorParsed(false);
    setTorKey(null);
    // and the shape read out of that document, so a fresh project is not offered the last
    // one’s format to save again
    setTorSections([]);
    setTemplateSaved(null);
    setSavingTemplate(false);
    setTorError(null);
    setTorParsing(false);
    setTorDragging(false);
    setConfidence({});
    // the paste-a-brief panel
    setPasteOpen(false);
    setPastedText('');
    // AI consultant suggestions from the Assign step
    setAiRecs(null);
    setAiRecsLoading(false);
    // a half-typed note that was never added
    setNewNoteContent('');
  };

  const handleStartNewProject = () => {
    resetForm();
    localStorage.removeItem(draftKey);
    setShowDraftPromptModal(false);
  };

  const handleContinueDraft = () => {
    // Cleared first for the same reason: a draft holds form fields, not the TOR badges or the
    // uploaded file, so anything left over would be attributed to a draft that never had it.
    resetForm();
    try {
      const draft = JSON.parse(localStorage.getItem(draftKey));
      if (draft) {
        // Keep the sub-project locked to its parent even when resuming a draft.
        if (initialParentId) draft.parentProjectId = String(initialParentId);
        setFormData(draft);
      }
    } catch (e) {
      console.error("Failed to load draft", e);
    }
    setShowDraftPromptModal(false);
  };

  // The footer button and the bar at the top now do the SAME thing — a named draft on the
  // server. Before this, one wrote a single browser-local slot and the other saved a real draft,
  // under the same words on the same screen, so which button you pressed silently decided whether
  // your work survived changing laptop.
  //
  // DraftBar hands its save function up here on mount; the footer button calls it. One
  // implementation, so the two cannot drift.
  const draftSaveRef = useRef(null);

  const handleSaveDraft = async () => {
    // Kept as a local safety net too — it costs nothing and covers a browser reload before the
    // request lands.
    try { localStorage.setItem(draftKey, JSON.stringify(collectDraft())); } catch { /* quota */ }
    if (draftSaveRef.current) {
      await draftSaveRef.current();
      return;                       // DraftBar shows its own "Saved as ..." confirmation
    }
    setAlertMessage('Draft saved.');
    setIsAlertError(false);
    setShowAlertModal(true);
  };

  const [isCreating, setIsCreating] = useState(false)

  const handleSubmitProject = async (e) => {
    e.preventDefault();
    // Per-step validation — block advancing (stay on this step) if something's wrong,
    // instead of letting invalid data through to the final "Create" click.
    if (step < 4) {
      if (step === 1) {
        if (!formData.name?.trim()) {
          setAlertMessage('Project name is required.'); setIsAlertError(true); setShowAlertModal(true); return;
        }
        // Duplicate-name check happens HERE (on Next from Details) so it stops early —
        // not after they've filled everything and clicked Create.
        if (!editingProject) {
          try {
            const chk = await api.get('/projects/check-name', { params: { name: formData.name } });
            if (chk.data?.exists) {
              if (!window.confirm(`A project named "${formData.name}" already exists. Do you still want to create another project with the same name?`)) {
                return; // stay on step 1 so they can rename
              }
            }
          } catch { /* non-fatal — proceed if the check is unavailable */ }
        }
      }
      if (step === 2) {
        // Dates are mandatory for a project.
        if (!formData.startDate || !formData.endDate) {
          setAlertMessage('Start date and end date are both required.'); setIsAlertError(true); setShowAlertModal(true); return;
        }
        if (new Date(formData.endDate) < new Date(formData.startDate)) {
          setAlertMessage('End date must be on or after the start date.'); setIsAlertError(true); setShowAlertModal(true); return;
        }
        // Don't let a sub-project advance with an end date past its parent program — the
        // backend rejects it, so catch it here where the field can still be corrected.
        if (parentEndDate && new Date(formData.endDate) > new Date(parentEndDate)) {
          setAlertMessage(`A sub-project can't end after its parent program. "${selectedParent?.name}" ends ${formatDate(parentEndDate)} — pick that date or earlier.`);
          setIsAlertError(true); setShowAlertModal(true); return;
        }
      }
      if (step === 3) {
        // Every milestone that has a title must also have a deadline.
        const msMissingDate = (formData.milestones || []).filter(m => (m.description || m.title || '').trim() && !m.deadline);
        if (msMissingDate.length > 0) {
          setAlertMessage('Every milestone must have a deadline.'); setIsAlertError(true); setShowAlertModal(true); return;
        }
        if (milestoneIssue) {
          setAlertMessage(milestoneIssue); setIsAlertError(true); setShowAlertModal(true); return;
        }
        // LEAVING WITH NONE IS A DECISION, NOT AN OVERSIGHT. The panel above catches this when a
        // document was read; a project typed in by hand reached this step with an empty list and
        // nothing said so. A project without milestones cannot be offered to anybody, so nothing
        // can be delivered or invoiced against it — it looks finished and is not. Asked once,
        // and taking "yes" for an answer.
        if (!editingProject && (formData.milestones || []).length === 0) {
          if (!window.confirm(
            'This project has no milestones.\n\n' +
            'Without at least one, it cannot be offered to a consultant, so nothing can be ' +
            'delivered or invoiced against it. You can add them later from the project itself.\n\n' +
            'Continue without milestones?'
          )) return;
        }
      }
      setStep(prev => prev + 1);
      return;
    }

    // Block milestones that break the project's budget/timeline before any save.
    if (milestoneIssue) {
      setAlertMessage(milestoneIssue);
      setIsAlertError(true);
      setShowAlertModal(true);
      return;
    }

    // The Assign step collects everyone into teamMemberIds. Milestones only get OFFERED to
    // a CONSULTANT (which notifies them + makes the project visible to them), so derive the
    // chosen consultant(s) from the selected team members.
    const selectedConsultantIds = (formData.teamMemberIds || []).filter(id => {
      const u = users.find(x => x.id === id)
      return u && (u.role === 'Consultant' || u.role === 'consultant')
    })

    // A project must have a consultant to be created (Bharat #24) — block otherwise.
    // (Skip this on edit; an existing project's consultant is managed via Offer Milestones.)
    if (!editingProject && selectedConsultantIds.length === 0 && !formData.consultantId) {
      setAlertMessage('A project must have at least one consultant assigned. Please add a consultant in the Assign step before creating.')
      setIsAlertError(true); setShowAlertModal(true)
      return
    }

    if (onSubmit) {
      setIsCreating(true)
      try {
        const submissionData = {
          ...formData,
          totalBudget: parseFloat(formData.estimatedBudget) || 0,
          approvedBudget: 0,
          // Prefer an explicitly-set consultantId; else the first consultant picked in Assign.
          consultantId: formData.consultantId || selectedConsultantIds[0] || '',
          consultantIds: selectedConsultantIds,
        };
        await onSubmit(submissionData);
        localStorage.removeItem(draftKey);
        onClose();
      } catch {
        // keep modal open so user sees the error in the parent
      } finally {
        setIsCreating(false)
      }
    }
  };

  const handleAddMilestone = () => {
    setFormData(prev => ({
      ...prev,
      milestones: [...prev.milestones, { description: '', status: 'Pending', amount: '', deadline: '', briefFile: null, mode: 'fixed' }]
    }));
  };

  const [memberSearch, setMemberSearch] = useState({});   // { role: query }
  const [memberOpen, setMemberOpen] = useState({});       // { role: bool } — dropdown open on click/focus
  const toggleMember = (uid) => {
    setFormData(prev => {
      const has = (prev.teamMemberIds || []).includes(uid);
      return { ...prev, teamMemberIds: has ? prev.teamMemberIds.filter(x => x !== uid) : [...(prev.teamMemberIds || []), uid] };
    });
  };

  // One searchable multi-select dropdown per role (Project Managers / Consultants / Finance).
  const renderRolePicker = (title, roleName) => {
    const candidates = Array.isArray(users) ? users.filter(u => u.role === roleName) : [];
    const q = (memberSearch[roleName] || '').trim().toLowerCase();
    const isConsultantRole = roleName === 'Consultant';
    // Consultants are also filterable by specialization, country/nationality and experience —
    // so a PM can find the right person fast without knowing their name.
    const consultantMeta = (c) => [
      c.specialization,
      c.country || c.nationality,
      (c.years_of_experience != null && c.years_of_experience !== '') ? `${c.years_of_experience} years` : '',
    ].filter(Boolean);
    const matches = candidates.filter(c => {
      const hay = [c.full_name, c.name, c.email, ...(isConsultantRole ? consultantMeta(c) : [])]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
    const selected = candidates.filter(c => (formData.teamMemberIds || []).includes(c.id));
    return (
      <div>
        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          {title} <span className="font-normal text-gray-400">(search & select multiple)</span>
        </label>
        {isConsultantRole && (
          <p className="text-[11px] text-gray-400 mb-1">Tip: filter by name, email, specialization, country, or years of experience.</p>
        )}
        {selected.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {selected.map(u => (
              <span key={u.id} className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2.5 py-1">
                {u.full_name || u.name || `User #${u.id}`}
                <button type="button" onClick={() => toggleMember(u.id)} className="ml-0.5 text-blue-400 hover:text-red-500">✕</button>
              </span>
            ))}
          </div>
        )}
        <div className="relative">
          <input
            type="text"
            value={memberSearch[roleName] || ''}
            onChange={(e) => setMemberSearch(s => ({ ...s, [roleName]: e.target.value }))}
            onFocus={() => setMemberOpen(s => ({ ...s, [roleName]: true }))}
            onBlur={() => setTimeout(() => setMemberOpen(s => ({ ...s, [roleName]: false })), 150)}
            placeholder={isConsultantRole ? 'Search by name, specialization, country or experience…' : `Search ${title.toLowerCase()} by name or email…`}
            className={`w-full px-3 py-2 pr-9 rounded-lg border text-sm ${isDark ? 'border-gray-600 bg-gray-800 text-white' : 'border-gray-200 bg-white text-gray-900'}`}
          />
          <button
            type="button"
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setMemberOpen(s => ({ ...s, [roleName]: !s[roleName] }))}
            className={`absolute inset-y-0 right-0 flex items-center px-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
            aria-label={`Show all ${title.toLowerCase()}`}
          >
            <svg className={`w-4 h-4 transition-transform ${memberOpen[roleName] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </button>
        </div>
        {(memberOpen[roleName] || q) && (
          <div className={`max-h-48 overflow-y-auto rounded-lg border mt-1 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            {matches.map(c => {
              const isSel = (formData.teamMemberIds || []).includes(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => toggleMember(c.id)}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between border-b last:border-0 ${isDark ? 'border-gray-700 hover:bg-gray-800' : 'border-gray-100 hover:bg-blue-50'} ${isSel ? (isDark ? 'bg-gray-800' : 'bg-blue-50') : ''}`}
                >
                  <span className="min-w-0 truncate">
                    {c.full_name || c.name}{c.email ? <span className="text-gray-400"> · {c.email}</span> : null}
                    {isConsultantRole && consultantMeta(c).length > 0 && (
                      <span className="block text-[11px] text-gray-400 truncate">{consultantMeta(c).join(' · ')}</span>
                    )}
                  </span>
                  {isSel && <span className="text-blue-600 text-xs shrink-0">✓ Added</span>}
                </button>
              );
            })}
            {matches.length === 0 && <p className="px-3 py-2 text-xs text-gray-400">{q ? `No ${title.toLowerCase()} match “${memberSearch[roleName]}”.` : `No ${title.toLowerCase()} available.`}</p>}
          </div>
        )}
      </div>
    );
  };

  const handledeleteMilestone = (index) => {
    setFormData(prev => ({
      ...prev,
      milestones: prev.milestones.filter((_, i) => i !== index)
    }));
  };

  const handleUpdateMilestone = (index, field, value) => {
    setFormData(prev => {
      const newMilestones = [...prev.milestones];
      newMilestones[index] = { ...newMilestones[index], [field]: value };
      return { ...prev, milestones: newMilestones };
    });
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = files.map(f => ({
      name: f.name,
      url: URL.createObjectURL(f), // Temporary URL for preview
      type: f.type,
      file: f,                     // keep the raw File so it can actually be uploaded on save
    }));
    setFormData(prev => ({
      ...prev,
      files: [...prev.files, ...newFiles]
    }));
  };

  const handleAddNote = (e) => {
    e.preventDefault(); // Prevent form submission if called via Enter
    if (!newNoteContent.trim()) return;

    const note = {
      content: newNoteContent,
      user: currentUser?.name || 'User',
      timestamp: new Date().toISOString()
    };

    setFormData(prev => ({
      ...prev,
      notes: [...prev.notes, note]
    }));
    setNewNoteContent('');
  };

  const steps = [
    { id: 1, label: 'Details', icon: FileText },
    { id: 2, label: 'Schedule', icon: Calendar },
    { id: 3, label: 'Budget & Files', icon: DollarSign },
    { id: 4, label: 'Assign', icon: Users }
  ];

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-5 animate-fafadeIn">
            {/* ── TOR Quick Start ──────────────────────────────────────── */}
            {!editingProject && (
              <div className={`p-4 rounded-2xl border-2 ${torParsed
                ? (isDark ? 'border-green-700 bg-green-900/20' : 'border-green-300 bg-green-50')
                : (isDark ? 'border-blue-700 bg-blue-900/20' : 'border-blue-300 bg-blue-50')}`}>
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${torParsed ? 'bg-green-500/20' : 'bg-blue-500/20'}`}>
                    {torParsed
                      ? <CheckCircle2 className="w-5 h-5 text-green-600" />
                      : <Zap className="w-5 h-5 text-blue-600" />}
                  </div>
                  <div className="flex-1">
                    <h4 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {torParsed ? 'TOR processed — fields auto-filled below' : 'Quick Start: Upload your TOR (.docx or .pdf)'}
                    </h4>
                    <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {torParsed
                        ? 'Review the auto-filled fields. Anything marked red is missing and must be filled manually.'
                        : 'Drag and drop a Terms of Reference document and we will auto-fill all project fields — including milestones.'}
                    </p>

                    {/* KEEP THIS DOCUMENT'S SHAPE.
                        A client's Terms of Reference has a house format: the headings they use and the
                        stages they ask for. Read once and discarded, the next project for the same client
                        starts from nothing again. Offered after a document has been read, because only
                        then is there a shape to keep. */}
                    {torParsed && !templateSaved && (
                      <div className="mt-3">
                        <button
                          type="button"
                          onClick={saveTorAsTemplate}
                          disabled={savingTemplate}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${isDark
                            ? 'border-gray-600 text-gray-200 hover:bg-gray-800'
                            : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'} disabled:opacity-50`}
                        >
                          {savingTemplate ? 'Saving…' : 'Save this format as a template'}
                        </button>
                        <span className={`ml-2 text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                          Its headings and stages, with deadlines taken off.
                        </span>
                      </div>
                    )}
                    
                    {templateSaved && (
                      <p className="mt-3 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                        {templateSaved}
                      </p>
                    )}
                    
                    {!torParsed && (
                      <div
                        onDragOver={e => { e.preventDefault(); setTorDragging(true); }}
                        onDragLeave={() => setTorDragging(false)}
                        onDrop={e => {
                          e.preventDefault();
                          setTorDragging(false);
                          const f = e.dataTransfer.files[0];
                          if (f) handleTorUpload(f);
                        }}
                        onClick={() => torFileRef.current?.click()}
                        className={`mt-3 border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-colors ${torDragging
                          ? (isDark ? 'border-blue-400 bg-blue-900/30' : 'border-blue-500 bg-blue-100')
                          : (isDark ? 'border-gray-600 bg-gray-800/50 hover:bg-gray-800' : 'border-gray-300 bg-white hover:bg-gray-50')}`}
                      >
                        {torParsing ? (
                          <div className="flex items-center justify-center gap-2">
                            <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{t('extracting_fields')}</span>
                          </div>
                        ) : (
                          <>
                            <Upload className={`w-7 h-7 mx-auto mb-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                            <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                              Drag &amp; drop TOR file or <span className="text-blue-600">browse</span>
                            </p>
                            <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>.docx or .pdf · max 50 MB</p>
                          </>
                        )}
                        <input
                          ref={torFileRef}
                          type="file"
                          accept=".docx,.pdf"
                          className="hidden"
                          onChange={e => e.target.files[0] && handleTorUpload(e.target.files[0])}
                        />
                      </div>
                    )}

                    {torParsed && (
                      <button
                        type="button"
                        onClick={() => { setTorParsed(false); setConfidence({}); setTorKey(null); }}
                        className="mt-2 text-xs text-blue-600 hover:underline"
                      >
                        Upload a different TOR
                      </button>
                    )}

                    {/* Or paste the brief. A .docx is the exception, not the rule — most
                        projects arrive as an email or a message, and making somebody save that
                        as a Word file first is a step with no purpose. Same extractor, same
                        fields, same confidence badges. */}
                    {!torParsed && (
                      <div className="mt-3">
                        {!pasteOpen ? (
                          <button
                            type="button"
                            onClick={() => setPasteOpen(true)}
                            className="text-xs font-medium text-blue-600 hover:underline"
                          >
                            No document yet? Describe what you need — type it or say it →
                          </button>
                        ) : (
                          <div>
                            <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                              Describe the work in your own words — or paste an email, a message or a scope note
                            </label>
                            {/* Mam's question was "what fields do they need to provide?". This is the answer, in
                                front of the box rather than in a manual nobody opens. */}
                            <p className={`text-xs mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              Worth mentioning: what the work is and why, who you need, roughly when it starts and
                              ends, the budget, and what they must hand over. Anything you leave out you can fill in
                              on the form afterwards.
                            </p>
                            <textarea
                              value={pastedText}
                              onChange={(e) => setPastedText(e.target.value)}
                              rows={6}
                              placeholder="e.g. Hi, we need a consultant for the Kenya newborn care mentorship. Runs 10 May to 14 June, budget around $18,000. Deliverables: inception report by 20 May, training workshop early June, final report by 14 June."
                              className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${isDark
                                ? 'border-gray-700 bg-gray-900 text-white placeholder-gray-600'
                                : 'border-gray-200 bg-white text-gray-900 placeholder-gray-400'}`}
                            />
                            <div className="flex items-center gap-2 mt-2">
                              {/* SPEAK IT. Offered only where the browser can actually listen, so nobody is given a
                                  button that does nothing. Everything here still works by typing. */}
                              {speechSupported && (
                                <button
                                  type="button"
                                  onClick={() => toggleDictation(setPastedText)}
                                  title={listening ? 'Stop listening' : 'Say it instead of typing'}
                                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${listening
                                    ? 'bg-red-50 border-red-300 text-red-700 animate-pulse'
                                    : (isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800'
                                              : 'border-gray-300 text-gray-700 hover:bg-gray-50')}`}
                                >
                                  {listening ? '■ Stop listening' : '🎤 Say it'}
                                </button>
                              )}
                              {speechError && (
                                <span className="text-xs text-red-600">{speechError}</span>
                              )}
                              <button
                                type="button"
                                onClick={handlePastedText}
                                disabled={torParsing || pastedText.trim().length < 40}
                                className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                {torParsing ? 'Reading…' : 'Fill the form from this'}
                              </button>
                              <button
                                type="button"
                                onClick={() => { setPasteOpen(false); setTorError(null); }}
                                className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} hover:underline`}
                              >
                                Cancel
                              </button>
                              <span className={`text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                Everything it fills in is editable — check it before you submit.
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {torError && (
                      <div className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{torError}</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ── WHAT THE READER DID NOT FIND ──────────────────────────────────
                Shown only after something has actually been read, and only while a milestone is
                still missing. A project with no milestones cannot be offered to anybody, so it
                cannot be delivered or invoiced — it looks finished and is not. */}
            {torParsed && !msGapDismissed && (formData.milestones || []).length === 0 && (
              <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 space-y-3">
                <div>
                  <p className="text-sm font-semibold text-amber-900">No milestones were found</p>
                  <p className="text-xs text-amber-800 mt-0.5">
                    A project without milestones cannot be offered to a consultant, so nothing can be
                    delivered or invoiced against it. You can add them now, or add them later from
                    the project itself.
                  </p>
                </div>

                <textarea
                  rows={3}
                  value={msGapText}
                  onChange={(e) => setMsGapText(e.target.value)}
                  placeholder="One per line — e.g. Inception report by 20 May. Training workshop 4 June. Final report by 14 June."
                  className="w-full text-sm rounded-lg border border-amber-300 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                />

                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => toggleDictation(setMsGapText)}
                    className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg border border-amber-300 bg-white hover:bg-amber-100"
                  >
                    {listening ? '■ Stop listening' : '🎤 Say it'}
                  </button>
                  <button
                    type="button"
                    onClick={applyMilestoneText}
                    disabled={msGapParsing}
                    className="text-xs font-semibold px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white"
                  >
                    {msGapParsing ? 'Reading…' : 'Add these milestones'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setMsGapDismissed(true)}
                    className="text-xs text-amber-800 hover:underline"
                  >
                    Continue without milestones
                  </button>
                </div>

                {speechError && <p className="text-[11px] text-red-600">{speechError}</p>}
                {msGapError && <p className="text-[11px] text-red-700">{msGapError}</p>}
              </div>
            )}

            {/* THE OTHER THINGS A TOR OFTEN LEAVES OUT. Asked here, beside the milestones, while
                the person still has the document in mind — rather than discovered as a validation
                error after they press submit. Only the genuinely empty ones appear. */}
            {torParsed && (!formData.startDate || !formData.endDate || !formData.priority) && (
              <div className="rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3">
                <p className="text-xs font-semibold text-amber-900 mb-2">Also not found — worth setting now</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {!formData.startDate && (
                    <div>
                      <label className="block text-[11px] font-medium text-amber-900 mb-1">Start date</label>
                      <input type="date" value={formData.startDate || ''}
                        onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                        className="w-full text-sm rounded-lg border border-amber-300 bg-white px-2 py-1.5" />
                    </div>
                  )}
                  {!formData.endDate && (
                    <div>
                      <label className="block text-[11px] font-medium text-amber-900 mb-1">End date</label>
                      <input type="date" value={formData.endDate || ''}
                        onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                        className="w-full text-sm rounded-lg border border-amber-300 bg-white px-2 py-1.5" />
                    </div>
                  )}
                  {!formData.priority && (
                    <div>
                      <label className="block text-[11px] font-medium text-amber-900 mb-1">Priority</label>
                      <select value={formData.priority || ''}
                        onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                        className="w-full text-sm rounded-lg border border-amber-300 bg-white px-2 py-1.5">
                        <option value="">Choose…</option>
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Split the budget once there is something to split it across. Offered rather than
                done automatically: the figures may already be right, and silently rewriting
                numbers somebody typed would be worse than leaving them alone. */}
            {(formData.milestones || []).length > 1 && (parseFloat(formData.estimatedBudget) || 0) > 0 && (
              <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                <p className="text-xs text-blue-900">
                  Split <b>{formData.estimatedBudget}</b> evenly across <b>{(formData.milestones || []).length}</b> milestones?
                  <span className="block text-[11px] text-blue-700 mt-0.5">
                    Every amount stays editable — this is a starting point, not a decision.
                  </span>
                </p>
                <button
                  type="button"
                  onClick={splitBudgetEvenly}
                  className="text-xs font-semibold px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white shrink-0"
                >
                  Split evenly
                </button>
              </div>
            )}

            <div>
              <div className="flex items-center">
                <label className={`text-sm font-semibold flex items-center gap-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Project Name <span className="text-red-500">*</span>
                  <Badge flag={confidence.name} />
                </label>
              </div>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('ph_project_name')}
                required
                className={`mt-2 w-full px-4 py-3 rounded-xl border-2 transition-all focus:outline-none focus:ring-4 focus:ring-blue-500/10 ${isDark
                  ? 'border-gray-700 bg-gray-800/50 text-white placeholder-gray-500 focus:border-blue-400'
                  : 'border-gray-200 bg-gray-50 text-gray-900 placeholder-gray-400 focus:border-blue-500'}`}
              />
            </div>
            {editingProject && (
              <FormInput
                label="Project ID (system-generated)"
                value={formData.projectNumber}
                disabled
                isDark={isDark}
              />
            )}
            <FormInput
              label="Client / External Reference (Optional)"
              value={formData.external_ref}
              onChange={(e) => setFormData({ ...formData, external_ref: e.target.value })}
              placeholder={t('ph_external_ref')}
              isDark={isDark}
            />
            {/* ── Or start from a template ──────────────────── */}
            {/* Hidden once a TOR has filled the form or milestones already exist, because
                applying a template replaces the milestone list and there would be work to
                lose. Nothing here can overwrite something somebody typed. */}
            {/* Not when "+ New" already offered them. Asking the same question twice is
                what Dhiraj hit: he chose a template at the door and the form laid
                the identical list out underneath it. */}
            {!editingProject && !templateAlreadyOffered && !torParsed && templates.length > 0
              && !(formData.milestones || []).some(m => (m.description || '').trim()) && (
              <div className={`p-4 rounded-2xl border ${isDark ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50'}`}>
                <h4 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  No TOR yet? Start from a template
                </h4>
                <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Fills in the stages this kind of work usually goes through. You still set the
                  dates and the money.
                </p>
                <div className="mt-3 grid sm:grid-cols-2 gap-2">
                  {templates.map(tmpl => (
                    <button
                      key={tmpl.id}
                      type="button"
                      onClick={() => applyTemplate(tmpl)}
                      className={`text-left rounded-xl border px-3 py-2 transition ${isDark
                        ? 'border-gray-700 bg-gray-900/40 hover:bg-gray-800'
                        : 'border-gray-200 bg-white hover:bg-gray-50'}`}
                    >
                      <span className={`block text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                        {tmpl.name}
                      </span>
                      {tmpl.description && (
                        <span className={`block text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {tmpl.description}
                        </span>
                      )}
                      <span className={`block text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {(tmpl.milestones || []).length} stage{(tmpl.milestones || []).length === 1 ? '' : 's'}
                        {tmpl.organisation_id ? ' · from your organisation' : ''}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Said plainly, because the milestones appear two steps later and somebody who
                does not know where they came from will wonder what added them. */}
            {usedTemplate && (
              <div className={`px-4 py-2.5 rounded-xl border text-xs flex items-center gap-2 ${isDark
                ? 'border-green-800 bg-green-900/20 text-green-300'
                : 'border-green-200 bg-green-50 text-green-800'}`}>
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                <span>
                  Started from <strong>{usedTemplate.name}</strong> — {(usedTemplate.milestones || []).length} milestone
                  {(usedTemplate.milestones || []).length === 1 ? '' : 's'} added on the Milestones step. Each still needs a deadline.
                </span>
              </div>
            )}

            {/* v2 nested projects: optionally nest this under a parent program */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('parent_project')}</label>
              <select
                value={formData.parentProjectId || ''}
                onChange={(e) => setFormData({ ...formData, parentProjectId: e.target.value })}
                disabled={!!initialParentId}
                className={`w-full border rounded-lg px-3 py-2 text-sm ${isDark ? 'bg-gray-800 border-gray-600 text-gray-100' : 'bg-white border-gray-300'} ${initialParentId ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                <option value="">— None (top-level project) —</option>
                {parentOptions.map(p => (
                  <option key={p.id} value={p.id}>{p.name}{p.project_number ? ` (${p.project_number})` : ''}</option>
                ))}
              </select>
              <p className={`text-[11px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                {initialParentId
                  ? '⭐ Creating a sub-project under this program — it keeps its own milestones, team and contracts, and you lead it.'
                  : 'Make this a sub-project of a larger program. It keeps its own milestones, team and contracts.'}
              </p>
            </div>
            <FormInput
              label="Description"
              required
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={t('ph_describe_project')}
              rows={3}
              isDark={isDark}
            />
            <FormInput
              label="Job Title (Optional)"
              value={formData.jobTitle}
              onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
              placeholder={t('ph_role_example')}
              isDark={isDark}
            />
            <FormInput
              label="Purpose of Position (Optional)"
              value={formData.purposeOfPosition}
              onChange={(e) => setFormData({ ...formData, purposeOfPosition: e.target.value })}
              placeholder={t('ph_describe_purpose')}
              rows={3}
              isDark={isDark}
            />
          </div>
        );
      case 2: {
        // Validate each date and explain exactly what's wrong, echoing what they entered.
        const isISO = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v || '') && !isNaN(new Date(v));
        const startErr = formData.startDate && !isISO(formData.startDate)
          ? `"${formData.startDate}" isn't a valid date — type it like 07-Jun-2026.`
          : '';
        let endErr = '';
        if (formData.endDate && !isISO(formData.endDate)) {
          endErr = `"${formData.endDate}" isn't a valid date — type it like 07-Jun-2026.`;
        } else if (isISO(formData.startDate) && isISO(formData.endDate) && new Date(formData.endDate) < new Date(formData.startDate)) {
          endErr = `End date (${formatDate(formData.endDate)}) is before the start date (${formatDate(formData.startDate)}). Pick a later end date.`;
        } else if (parentEndDate && isISO(formData.endDate) && new Date(formData.endDate) > new Date(parentEndDate)) {
          // A sub-project can finish early, but never after its parent program.
          endErr = `A sub-project can't end after its parent program. "${selectedParent?.name}" ends ${formatDate(parentEndDate)} — pick that date or earlier.`;
        }
        return (
          <div className="space-y-5 animate-fafadeIn">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <DateField
                label="Start Date"
                required
                value={formData.startDate}
                error={startErr}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                isDark={isDark}
              />
              <DateField
                label="End Date"
                required
                value={formData.endDate}
                error={endErr}
                max={parentEndDate || undefined}
                hint={parentEndDate
                  ? `Inherited from "${selectedParent?.name}" (ends ${formatDate(parentEndDate)}). You can finish earlier, but not later.`
                  : undefined}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                isDark={isDark}
              />
            </div>

            <FormSelect
              label="Priority"
              required
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              placeholder={t('ph_select_priority')}
              options={[
                { value: 'High', label: '🔴 High Priority' },
                { value: 'Medium', label: '🟡 Medium Priority' },
                { value: 'Low', label: '🟢 Low Priority' }
              ]}
              isDark={isDark}
            />
          </div>
        );
      }
      case 3:
        return (
          <div className="space-y-6 animate-fafadeIn">
            {/* Budget model selector */}
            <div>
              <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                Budget Type
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { key: 'Fixed', title: 'Total / Fixed', desc: 'Agreed lump sum, paid per milestone.' },
                  { key: 'Actual', title: 'Actual / Reimbursable', desc: 'Consultant claims actual amount + attaches bills.' },
                  { key: 'Hybrid', title: 'Hybrid', desc: 'Fixed fee + actual expenses on top.' },
                ].map(opt => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setFormData({ ...formData, budgetType: opt.key })}
                    className={`text-left rounded-xl border p-3 transition-colors ${
                      formData.budgetType === opt.key
                        ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                        : (isDark ? 'border-gray-700 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300')
                    }`}
                  >
                    <p className={`text-sm font-semibold ${formData.budgetType === opt.key ? 'text-blue-700' : (isDark ? 'text-gray-100' : 'text-gray-900')}`}>{opt.title}</p>
                    <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{opt.desc}</p>
                  </button>
                ))}
              </div>
              {formData.budgetType !== 'Fixed' && (
                <p className="text-xs text-amber-600 mt-2">
                  {formData.budgetType === 'Actual'
                    ? 'Reimbursable: the amounts below are estimates — the consultant claims the actual amount (with bills) at invoice time.'
                    : 'Hybrid: set each milestone below as Fixed (enter the amount) or Actuals (claimed with bills at invoice time) — a project can contain both.'}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Fixed → enter the lump-sum budget. */}
              {formData.budgetType === 'Fixed' && (
                <FormInput
                  label="Estimated Budget ($)"
                  required type="number" min="1" step="0.01"
                  value={formData.estimatedBudget}
                  onChange={(e) => setFormData({ ...formData, estimatedBudget: e.target.value })}
                  placeholder={t('ph_zero')} isDark={isDark}
                />
              )}

              {/* Actual → no amount is entered; the field is locked to "Actuals". */}
              {formData.budgetType === 'Actual' && (
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{tc('labels.budget')}</label>
                  <input
                    type="text" value="Actuals" disabled readOnly
                    className={`w-full px-3 py-2 rounded-lg border text-sm font-semibold cursor-not-allowed ${isDark ? 'border-gray-700 bg-gray-800 text-amber-400' : 'border-gray-200 bg-gray-100 text-amber-700'}`}
                  />
                </div>
              )}

              {/* Hybrid → an Estimated Budget (the fixed baseline) plus an "Add actuals"
                  add-on. The real Fixed/Actuals split is chosen per-milestone below. */}
              {formData.budgetType === 'Hybrid' && (
                <>
                  <FormInput
                    label="Estimated Budget ($)"
                    type="number" min="0" step="0.01"
                    value={formData.estimatedBudget}
                    onChange={(e) => setFormData({ ...formData, estimatedBudget: e.target.value })}
                    placeholder={t('ph_zero')} isDark={isDark}
                  />
                  {formData.hybridActuals ? (
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('actuals')}</label>
                      <div className="relative">
                        <input
                          type="text" value="Actuals · claimed with bills" disabled readOnly
                          className={`w-full px-3 py-2 pr-8 rounded-lg border text-sm font-semibold cursor-not-allowed ${isDark ? 'border-gray-700 bg-gray-800 text-amber-400' : 'border-gray-200 bg-gray-100 text-amber-700'}`}
                        />
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, hybridActuals: false })}
                          className="absolute inset-y-0 right-0 flex items-center px-2 text-gray-400 hover:text-red-500"
                          aria-label="Remove actuals"
                        >✕</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, hybridActuals: true })}
                        className={`w-full px-3 py-2 rounded-lg border border-dashed text-sm font-medium transition-colors ${isDark ? 'border-gray-600 text-blue-400 hover:border-blue-500 hover:bg-blue-900/20' : 'border-gray-300 text-blue-600 hover:border-blue-400 hover:bg-blue-50'}`}
                      >
                        + Add actuals
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
            {formData.budgetType === 'Actual' && (
              <p className="text-xs text-amber-600 -mt-3">
                No fixed amount is set — the consultant claims the actual amount (with bills attached) when submitting each milestone's invoice.
              </p>
            )}

            {/* Team members are chosen on the Assign step (last step). */}

            {/* Milestones Section */}
            <div className={`pt-6 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <CheckCircle2 className="w-5 h-5 text-blue-500" />
                  Project Milestones
                </h3>
                <button
                  type="button"
                  onClick={handleAddMilestone}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium 
                           ${isDark ? 'text-blue-400 bg-blue-900/30 hover:bg-blue-900/50' : 'text-blue-600 bg-blue-50 hover:bg-blue-100'} 
                           rounded-xl transition-all duration-200`}
                >
                  <Plus className="w-4 h-4" />
                  Add Milestone
                </button>
              </div>

              {milestoneIssue && (
                <div className="mb-3 text-xs rounded-lg border border-red-300 bg-red-50 text-red-700 px-3 py-2">
                  ⚠️ {milestoneIssue}
                </div>
              )}
              {!milestoneIssue && budgetNum > 0 && formData.budgetType !== 'Actual' && formData.milestones.length > 0 && (
                <p className="mb-2 text-[11px] text-gray-500">
                  Milestone total: ${milestonesTotal.toLocaleString()} of ${budgetNum.toLocaleString()} budget
                </p>
              )}
              <div className="space-y-3">
                {formData.milestones.length === 0 ? (
                  <div className={`text-center py-8 rounded-xl border-2 border-dashed ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                    <CheckCircle2 className={`w-10 h-10 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t('no_milestones')}</p>
                  </div>
                ) : (
                  formData.milestones.map((milestone, index) => (
                    <div key={index} className={`p-4 rounded-xl border group transition-all duration-200 ${isDark ? 'bg-gray-800/50 border-gray-700 hover:border-blue-600' : 'bg-gray-50 border-gray-200 hover:border-blue-300'}`}>
                      <div className="flex justify-between items-center mb-3">
                        <span className={`text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          Milestone {index + 1}
                          {milestone.locked && (
                            <span className="ml-2 text-[10px] font-medium border border-amber-200 bg-amber-50 text-amber-700 rounded-full px-2 py-0.5">
                              🔒 agreed with consultant — locked
                            </span>
                          )}
                        </span>
                        {!milestone.locked && (
                          <button
                            type="button"
                            onClick={() => handledeleteMilestone(index)}
                            className={`p-1.5 rounded-lg transition-all duration-200 ${isDark ? 'text-gray-400 hover:bg-red-900/30 hover:text-red-500' : 'text-gray-400 hover:bg-red-50 hover:text-red-500'}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      {/* Milestone title */}
                      <input
                        type="text"
                        value={milestone.description}
                        disabled={!!milestone.locked}
                        onChange={(e) => handleUpdateMilestone(index, 'description', e.target.value)}
                        className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/10 ${isDark ? 'border-gray-600 bg-gray-800 text-white focus:border-blue-500' : 'border-gray-200 bg-white text-gray-900 focus:border-blue-500'}`}
                        placeholder={t('ph_milestone_example')}
                      />
                      {/* Hybrid → each milestone is either Fixed (enter amount) or Actuals (claimed with bills). */}
                      {formData.budgetType === 'Hybrid' && (
                        <div className="mt-2 flex items-center gap-2">
                          <span className={`text-xs shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t('type')}</span>
                          <select
                            value={milestone.mode || 'fixed'}
                            onChange={(e) => handleUpdateMilestone(index, 'mode', e.target.value)}
                            className={`flex-1 px-3 py-1.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/10 ${isDark ? 'border-gray-600 bg-gray-800 text-white focus:border-blue-500' : 'border-gray-200 bg-white text-gray-900 focus:border-blue-500'}`}
                          >
                            <option value="fixed">{t('fixed_enter_amount')}</option>
                            <option value="actual">{t('actuals_claimed')}</option>
                          </select>
                        </div>
                      )}
                      {/* Payment amount (Fixed) OR locked "Actuals" indicator + Due date.
                          Actual projects: every milestone is reimbursable — no amount is entered. */}
                      <div className="mt-2 flex items-center gap-2">
                        <span className={`text-xs shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t('payment')}</span>
                        {(formData.budgetType === 'Actual' || (formData.budgetType === 'Hybrid' && (milestone.mode || 'fixed') === 'actual')) ? (
                          <input
                            type="text" value="Actuals · claimed with bills" disabled readOnly
                            className={`flex-1 px-3 py-1.5 rounded-lg border text-sm font-semibold cursor-not-allowed ${isDark ? 'border-gray-700 bg-gray-800 text-amber-400' : 'border-gray-200 bg-gray-100 text-amber-700'}`}
                          />
                        ) : (
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={milestone.amount || ''}
                            disabled={!!milestone.locked}
                            onChange={(e) => handleUpdateMilestone(index, 'amount', e.target.value)}
                            className={`flex-1 px-3 py-1.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/10 ${isDark ? 'border-gray-600 bg-gray-800 text-white focus:border-blue-500' : 'border-gray-200 bg-white text-gray-900 focus:border-blue-500'}`}
                            placeholder={t('ph_amount_example')}
                          />
                        )}
                        <span className={`text-xs shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Due</span>
                        <InlineDate
                          value={milestone.deadline || ''}
                          disabled={!!milestone.locked}
                          onChange={(iso) => handleUpdateMilestone(index, 'deadline', iso)}
                          isDark={isDark}
                        />
                      </div>
                      {/* System / Tentative Budget — reimbursable milestones only. PM/Admin-only cap;
                          the consultant's bills for this milestone can't exceed it (they never see it). */}
                      {(formData.budgetType === 'Actual' || (formData.budgetType === 'Hybrid' && (milestone.mode || 'fixed') === 'actual')) && (
                        <div className={`mt-2 p-2.5 rounded-lg border ${isDark ? 'border-amber-800/50 bg-amber-900/10' : 'border-amber-200 bg-amber-50/60'}`}>
                          <div className="flex items-center justify-between">
                            <span className={`text-xs font-medium ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>{t('system_tentative')} <span className="font-normal text-gray-400">· PM/Admin only</span></span>
                            <span className="text-xs font-bold text-amber-600">${Number(milestone.tentativeBudget || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                          <input
                            type="range" min="0" max="50000" step="500"
                            value={Number(milestone.tentativeBudget || 0)}
                            disabled={!!milestone.locked}
                            onChange={(e) => handleUpdateMilestone(index, 'tentativeBudget', e.target.value)}
                            className="w-full mt-1.5 accent-amber-500"
                          />
                          <div className="flex items-center gap-1.5 mt-1.5">
                            {[15000, 25000, 50000].map(v => (
                              <button
                                type="button" key={v}
                                onClick={() => handleUpdateMilestone(index, 'tentativeBudget', v)}
                                className={`px-2 py-0.5 text-[11px] rounded border ${Number(milestone.tentativeBudget) === v ? 'bg-amber-500 text-white border-amber-500' : (isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-800' : 'border-gray-300 text-gray-600 hover:bg-white')}`}
                              >${v.toLocaleString()}</button>
                            ))}
                            <input
                              type="number" min="0" step="0.01"
                              value={milestone.tentativeBudget ?? ''}
                              disabled={!!milestone.locked}
                              onChange={(e) => handleUpdateMilestone(index, 'tentativeBudget', e.target.value)}
                              placeholder={t('ph_custom')}
                              className={`flex-1 px-2 py-1 rounded border text-xs focus:outline-none ${isDark ? 'border-gray-600 bg-gray-800 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                            />
                          </div>
                          <p className="text-[10px] text-gray-400 mt-1">Bills for this milestone can't exceed this. Consultants never see it; if they hit the cap they're told to contact you.</p>
                        </div>
                      )}
                      {/* Brief / Instructions file — optional, uploaded after project creation */}
                      <div className="mt-2">
                        {milestone.briefFile ? (
                          <div className={`flex items-center justify-between px-3 py-1.5 rounded-lg border text-xs ${isDark ? 'bg-green-900/30 border-green-700 text-green-300' : 'bg-green-50 border-green-200 text-green-700'}`}>
                            <span>📎 {milestone.briefFile.name}</span>
                            <button type="button" onClick={() => handleUpdateMilestone(index, 'briefFile', null)} className="ml-2 text-red-500 hover:text-red-700">✕</button>
                          </div>
                        ) : (
                          <label className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border border-dashed cursor-pointer text-xs transition-colors ${isDark ? 'border-gray-600 text-gray-400 hover:border-blue-500 hover:text-blue-400' : 'border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-600'}`}>
                            <span>📤 Upload brief/instructions (optional PDF, DOCX…)</span>
                            <input
                              type="file"
                              accept=".pdf,.docx,.doc,.xlsx,.pptx,.txt"
                              className="hidden"
                              onChange={(e) => e.target.files?.[0] && handleUpdateMilestone(index, 'briefFile', e.target.files[0])}
                            />
                          </label>
                        )}
                      </div>
                      {/* Quick "add another" right under each milestone so you don't scroll up. */}
                      <button
                        type="button"
                        onClick={handleAddMilestone}
                        className={`mt-3 w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg border border-dashed text-xs font-medium transition-colors ${isDark ? 'border-gray-600 text-gray-400 hover:border-blue-500 hover:text-blue-400' : 'border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-600'}`}
                      >
                        <Plus className="w-3.5 h-3.5" /> Add another milestone
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* File Upload Section */}
            <div className={`pt-6 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-bold flex items-center gap-2 mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <Upload className="w-5 h-5 text-blue-500" />
                Project Files
              </h3>

              <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-xl cursor-pointer transition-all duration-200 group ${isDark ? 'border-gray-600 bg-gray-800/50 hover:bg-gray-800' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'}`}>
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Upload className={`w-8 h-8 group-hover:text-blue-500 transition-colors mb-2 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    <span className={`font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{t('click_to_upload')}</span> or drag and drop
                  </p>
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{t('any_file_type')}</p>
                </div>
                <input type="file" multiple onChange={handleFileUpload} className="hidden" />
              </label>

              {formData.files.length > 0 && (
                <div className="mt-4 space-y-2">
                  {formData.files.map((file, index) => (
                    <div key={index} className={`flex items-center justify-between p-3 rounded-xl text-sm ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                      <span className={`truncate flex-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{file.name}</span>
                      {file.url && (
                        <a href={file.url} target="_blank" rel="noopener noreferrer" className={`ml-3 text-sm font-medium hover:underline ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                          View
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Notes Section */}
            <div className={`pt-6 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-bold flex items-center gap-2 mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <MessageSquare className="w-5 h-5 text-blue-500" />
                Notes & Comments
              </h3>

              <div className="flex gap-3">
                <input
                  type="text"
                  placeholder={t('ph_new_note')}
                  className={`flex-1 px-4 py-3 rounded-xl border-2 focus:outline-none focus:ring-4 focus:ring-blue-500/10 transition-all ${isDark ? 'border-gray-700 bg-gray-800/50 text-white placeholder-gray-500 focus:border-blue-500' : 'border-gray-200 bg-gray-50 text-gray-900 placeholder-gray-400 focus:border-blue-500'}`}
                  value={newNoteContent}
                  onChange={(e) => setNewNoteContent(e.target.value)}
                  onKeyPress={(e) => { if (e.key === 'Enter') { handleAddNote(e); } }}
                />
                <button
                  type="button"
                  onClick={handleAddNote}
                  disabled={!newNoteContent.trim()}
                  className="px-5 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40"
                >
                  Add
                </button>
              </div>

              <div className="mt-4 space-y-3 max-h-40 overflow-y-auto">
                {formData.notes.length === 0 ? (
                  <p className={`text-sm text-center py-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t('no_notes')}</p>
                ) : (
                  formData.notes.map((note, index) => (
                    <div key={index} className={`p-4 rounded-xl ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                      <p className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{note.content}</p>
                      <p className={`text-xs mt-2 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                        By {note.user} • {formatDateTime(note.timestamp)}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        );
      case 4:
        return (
          <div className="space-y-5 animate-fafadeIn">
            <div className={`p-6 rounded-2xl border mb-6 bg-gradient-to-br ${isDark ? 'from-blue-900/20 to-indigo-900/20 border-blue-800/50' : 'from-blue-50 to-indigo-50 border-blue-100'}`}>
              <h4 className={`text-lg font-bold mb-2 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <Users className="w-5 h-5 text-blue-500" />
                Assign the Team
              </h4>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Add the people involved. Everyone you add is notified by email once the project is created and can open it from there. You can edit the team later too.
              </p>
            </div>

            {/* Admin: choose which Project Manager owns/runs this project */}
            {isAdmin && (
              <FormSelect
                label="Project Manager (owner)"
                value={formData.projectManagerId}
                onChange={(e) => setFormData({ ...formData, projectManagerId: e.target.value })}
                placeholder={t('ph_select_pm')}
                options={pmOptions.length > 0
                  ? pmOptions.map(p => ({ value: p.id, label: `${p.full_name || p.name || 'Unknown'}${p.email ? ' — ' + p.email : ''}` }))
                  : [{ value: '', label: 'No project managers available' }]
                }
                isDark={isDark}
              />
            )}

            {/* One searchable multi-select dropdown per role */}
            {isAdmin && renderRolePicker('Project Managers', 'Project Manager')}
            {renderRolePicker('Consultants', 'Consultant')}

            {/* AI consultant suggestions — matches the project brief against consultant profiles */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t('ai_suggest')}</span>
                <button type="button" onClick={fetchConsultantRecs} disabled={aiRecsLoading}
                  className="text-xs px-2.5 py-1 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50">
                  {aiRecsLoading ? 'Finding…' : '✨ Suggest consultants (AI)'}
                </button>
              </div>
              {aiRecs && aiRecs.length === 0 && !aiRecsLoading && (
                <p className="text-[11px] text-gray-400">No suggestions yet — add a project description on the Details step, or pick consultants manually above.</p>
              )}
              {aiRecs && aiRecs.length > 0 && (
                <div className="space-y-1.5 mt-1">
                  {aiRecs.map(rec => {
                    const added = (formData.teamMemberIds || []).includes(rec.consultant_id);
                    return (
                      <div key={rec.consultant_id} className={`flex items-start justify-between gap-2 p-2 rounded-lg border ${isDark ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-white'}`}>
                        <div className="min-w-0">
                          <p className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                            {rec.name}{rec.score != null && <span className="ml-2 text-[11px] font-semibold text-indigo-600">{rec.score}% match</span>}
                          </p>
                          {rec.specialization && <p className="text-[11px] text-gray-400">{rec.specialization}</p>}
                          {rec.reason && <p className={`text-[11px] mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{rec.reason}</p>}
                        </div>
                        <button type="button" onClick={() => toggleMember(rec.consultant_id)}
                          className={`shrink-0 text-xs px-2 py-1 rounded border ${added ? 'bg-green-50 border-green-200 text-green-700' : 'border-blue-200 text-blue-700 hover:bg-blue-50'}`}>
                          {added ? '✓ Added' : '+ Add'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {renderRolePicker('Finance', 'Finance')}

            {/* Per-milestone assignment — give specific milestones to specific consultants so
                each person only sees/works on their own (no clash). "Offer later" leaves it open. */}
            {(() => {
              const selCons = consultantsOptions.filter(c => (formData.teamMemberIds || []).includes(c.id));
              const namedMs = (formData.milestones || []).map((m, i) => ({ m, i })).filter(({ m }) => (m.description || m.title || '').trim());
              if (selCons.length === 0 || namedMs.length === 0) return null;
              return (
                <div className={`border rounded-xl p-4 ${isDark ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50'}`}>
                  <h5 className={`text-sm font-semibold mb-1 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{t('assign_milestones')}</h5>
                  <p className={`text-xs mb-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Give each milestone to a specific consultant — they'll only see and work on the ones assigned to them (no overlap). Leave “Offer later” to assign from the project's Team tab afterwards.
                  </p>
                  <div className="space-y-2">
                    {namedMs.map(({ m, i }) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className={`flex-1 text-sm truncate ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{(m.description || m.title).trim()}</span>
                        <select
                          value={m.assignConsultantId || ''}
                          onChange={(e) => handleUpdateMilestone(i, 'assignConsultantId', e.target.value)}
                          className={`w-56 px-2 py-1.5 rounded-lg border text-sm ${isDark ? 'border-gray-600 bg-gray-800 text-white' : 'border-gray-200 bg-white text-gray-900'}`}
                        >
                          <option value="">— Offer later —</option>
                          {selCons.map(c => <option key={c.id} value={c.id}>{c.full_name || c.name}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {(formData.teamMemberIds || []).length > 0 && (
              <div className={`border rounded-xl p-3 flex items-start gap-2 ${isDark ? 'bg-green-900/20 border-green-800/50' : 'bg-green-50 border-green-200'}`}>
                <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                <p className={`text-xs ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                  {formData.teamMemberIds.length} team member(s) will be added and emailed a link to this project.
                </p>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={editingProject ? 'Edit Project' : 'Create New Project'}
        size="large"
      >
        {/* Park this and start another. Only when creating: an existing project is saved by
            saving it, and offering to "draft" an edit would mean two versions of a live record. */}
        {!editingProject && (
          <div className="mb-4">
            <DraftBar
              kind="project"
              label={formData.name}
              collect={collectDraft}
              apply={applyDraft}
              context={{ project_id: initialParentId || null }}
              registerSave={fn => { draftSaveRef.current = fn }}
              onSaved={onDraftSaved}
              filesWarning
            />
          </div>
        )}

        {/* Enhanced Stepper */}
        <div className="relative mb-8">
          {/* Progress track — spans between the FIRST and LAST icon centers (each step sits in
              a 25%-wide column, so centers are at 12.5% and 87.5%). Insetting to those points
              stops the line overflowing past the end icons. */}
          <div className="absolute top-5 left-[12.5%] right-[12.5%] h-1 bg-gray-200 dark:bg-gray-700 rounded-full" />

          {/* Active Progress — fills the same 75%-wide track proportionally to the step. */}
          <div
            className="absolute top-5 left-[12.5%] h-1 rounded-full transition-all duration-500 ease-out"
            style={{
              width: `${((step - 1) / 3) * 75}%`,
              background: 'linear-gradient(90deg, #3b82f6, #6366f1)'
            }}
          />

          {/* Steps */}
          <div className="relative flex justify-between">
            {steps.map((s, idx) => {
              const StepIcon = s.icon;
              const isCompleted = step > s.id;
              const isCurrent = step === s.id;

              return (
                <div key={s.id} className="flex flex-col items-center" style={{ width: '25%' }}>
                  <button
                    type="button"
                    onClick={() => {
                      if (s.id < step) setStep(s.id);
                    }}
                    disabled={s.id > step}
                    className={`
                      relative w-10 h-10 rounded-xl flex items-center justify-center
                      transition-all duration-300 ease-out transform
                      ${isCompleted
                        ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/30 scale-100'
                        : isCurrent
                          ? (isDark ? 'bg-gray-800 border-2 border-blue-500 text-blue-400' : 'bg-white border-2 border-blue-500 text-blue-600') + ' shadow-lg shadow-blue-500/20 scale-110'
                          : (isDark ? 'bg-gray-800 border-2 border-gray-700 text-gray-500' : 'bg-gray-100 border-2 border-gray-200 text-gray-400')
                      }
                      ${s.id < step ? 'cursor-pointer hover:scale-105' : s.id > step ? 'cursor-not-allowed' : ''}
                    `}
                  >
                    {isCompleted ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      <StepIcon className="w-5 h-5" />
                    )}

                    {/* Pulse animation for current step */}
                    {isCurrent && (
                      <span className="absolute inset-0 rounded-xl animate-ping bg-blue-400 opacity-20" />
                    )}
                  </button>

                  <span className={`
                    mt-3 text-xs font-medium text-center transition-colors duration-300
                    ${isCurrent ? (isDark ? 'text-blue-400' : 'text-blue-600') : (isDark ? 'text-gray-400' : 'text-gray-500')}
                  `}>
                    {s.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <form ref={projectFormRef} onSubmit={handleSubmitProject}>
          {renderStep()}

          {/* Missing fields alert — shown when TOR has been parsed but some required fields are still empty */}
          {torParsed && missingFields.length > 0 && (
            <div className={`mt-6 p-4 rounded-xl border-2 ${isDark ? 'bg-amber-900/20 border-amber-700' : 'bg-amber-50 border-amber-300'}`}>
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className={`text-sm font-semibold ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>
                    {missingFields.length} required field{missingFields.length > 1 ? 's' : ''} still need attention before you can create the project:
                  </p>
                  <ul className="mt-2 text-xs space-y-0.5">
                    {missingFields.map(f => (
                      <li key={f.key} className={`flex items-center gap-1.5 ${isDark ? 'text-amber-200' : 'text-amber-700'}`}>
                        <span className="w-1 h-1 rounded-full bg-amber-500" />
                        {f.label}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Footer Actions */}
          <div className={`flex flex-col sm:flex-row justify-between items-center gap-4 mt-8 pt-6 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <button
              type="button"
              onClick={() => setStep(prev => Math.max(1, prev - 1))}
              disabled={step === 1}
              className={`
                px-5 py-2.5 rounded-xl font-medium transition-all duration-200
                ${step === 1
                  ? (isDark ? 'bg-gray-800 text-gray-600 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed')
                  : (isDark ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200')
                }
              `}
            >
              Previous
            </button>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleSaveDraft}
                className={`px-5 py-2.5 font-medium rounded-xl transition-all duration-200 border ${isDark ? 'bg-amber-900/30 text-amber-400 border-amber-800/50 hover:bg-amber-900/50' : 'bg-amber-50 text-amber-600 border-amber-200 hover:bg-amber-100'}`}
              >
                Save Draft
              </button>

              <button
                type="submit"
                disabled={isCreating}
                className={`
                  px-6 py-2.5 font-semibold rounded-xl transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]
                  shadow-lg flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none
                  ${step < 4
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-blue-500/25 hover:shadow-blue-500/40'
                    : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-green-500/25 hover:shadow-green-500/40'
                  }
                `}
              >
                {step < 4 ? (
                  <>{t('next')} <ChevronRight className="w-4 h-4" /></>
                ) : isCreating ? (
                  <span className="flex items-center gap-2">
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                    </svg>
                    {editingProject ? 'Saving…' : 'Creating…'}
                  </span>
                ) : (
                  editingProject ? 'Update Project' : 'Create Project'
                )}
              </button>
            </div>
          </div>
        </form>
      </Modal>

      {/* Draft Prompt Modal */}
      <Modal
        isOpen={showDraftPromptModal}
        onClose={() => setShowDraftPromptModal(false)}
        title={t('t_continue_draft')}
        size="small"
      >
        <div className="text-center py-4">
          <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${isDark ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
            <FileText className="w-8 h-8 text-blue-500" />
          </div>
          <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-sm`}>
            You have an unsaved project draft. Would you like to continue from where you left off?
          </p>
        </div>
        <div className="flex gap-3 mt-6">
          <button
            type="button"
            onClick={handleStartNewProject}
            className={`flex-1 px-4 py-2.5 font-medium rounded-xl transition-all ${isDark ? 'text-gray-300 bg-gray-800 hover:bg-gray-700' : 'text-gray-700 bg-gray-100 hover:bg-gray-200'}`}
          >
            Start Fresh
          </button>
          <button
            type="button"
            onClick={handleContinueDraft}
            className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-medium shadow-lg shadow-blue-500/25"
          >
            Continue Draft
          </button>
        </div>
      </Modal>

      {/* Alert Modal */}
      <Modal
        isOpen={showAlertModal}
        onClose={() => setShowAlertModal(false)}
        title={isAlertError ? "Attention Required" : "Success"}
        size="small"
      >
        <div className="text-center py-4">
          <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${isAlertError ? (isDark ? 'bg-red-900/30' : 'bg-red-50') : (isDark ? 'bg-green-900/30' : 'bg-green-50')}`}>
            {isAlertError ? (
              <AlertCircle className="w-8 h-8 text-red-500" />
            ) : (
              <CheckCircle2 className="w-8 h-8 text-green-500" />
            )}
          </div>
          <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{alertMessage}</p>
        </div>
        <button
          onClick={() => setShowAlertModal(false)}
          className={`w-full mt-4 px-4 py-2.5 font-medium rounded-xl transition-all shadow-lg ${isAlertError
            ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-blue-500/25'
            : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-green-500/25'
            }`}
        >
          OK
        </button>
      </Modal>

      <style jsx>{`
        @keyframes fafadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fafadeIn {
          animation: fafadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </>
  );
};

export default ProjectModal;
